// CUSTOM: personalised-utils.js
// Simplified utilities shared across personalised bundle system

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};

  /**
   * Customer ID Manager - Encapsulates customer ID access with a getter/setter closure
   * to prevent race conditions and accidental overwrites
   */
  const customerIdManager = (function() {
    // Internal storage for customer ID
    let _customerId = null;

    return {
      /**
       * Gets the current customer ID
       * @returns {string|null} The customer ID or null if not set
       */
      get() {
        return _customerId;
      },

      /**
       * Sets the customer ID
       * @param {string|null} id - The customer ID to set
       * @returns {boolean} Success status
       */
      set(id) {
        // Allow null/undefined to reset
        if (id === null || id === undefined) {
          _customerId = null;
          if (window.personalised?.debugMode) console.debug('[CustomerIdManager] Customer ID reset');
          return true;
        }
        
        // Validate ID format before setting
        if (typeof id === 'string' || typeof id === 'number') {
          _customerId = String(id);
          if (window.personalised?.debugMode) console.debug('[CustomerIdManager] Customer ID set successfully');
          return true;
        } else {
          if (window.personalised?.debugMode) console.warn('[CustomerIdManager] Invalid customer ID format:', typeof id);
          return false;
        }
      }
    };
  })();
  
  // Define customerIdManager on personalised namespace for internal use
  window.personalised.customerIdManager = customerIdManager;
  
  // Define property for backward compatibility
  Object.defineProperty(window.personalised, 'customerId', {
    get() {
      return customerIdManager.get();
    },
    set(value) {
      customerIdManager.set(value);
    },
    enumerable: true,
    configurable: true
  });

  /**
   * Session Manager module to centralize Recharge session management
   */
  const sessionManager = (function() {
    // Internal session storage
    let currentSession = null;
    // Promise memoization to prevent concurrent session fetches
    let _sessionPromise = null;
    
    /**
     * Validates if a session has the required properties for SDK v1.38.0
     * Note: Using loginShopifyAppProxy return value format
     * 
     * @param {Object} session - The session to validate
     * @returns {boolean} True if valid, false otherwise
     */
    const isValidSession = (session) => {
      return session && 
             typeof session === 'object' && 
             typeof session.apiToken === 'string' &&
             session.apiToken.length > 0;
    };
    
    return {
      /**
       * Gets the current Recharge session by calling loginShopifyAppProxy
       * Note: Uses loginShopifyAppProxy instead of getSession as per SDK v1.38.0 requirements
       * 
       * @param {Object|boolean} options - Options object or boolean for backwards compatibility
       * @param {boolean} [options.forceRefresh] - Force a new session even if one exists
       * @returns {Promise<Object|null>} The session object or null if unavailable
       */
      async getSession(options = false) {
        // Handle legacy boolean parameter for backwards compatibility
        const forceRefresh = typeof options === 'boolean' ? options : options.forceRefresh || false;
        
        // Check for session freshness flag
        const isSessionFresh = sessionStorage.getItem('personalised_session_fresh') === 'true';
        if (isSessionFresh) {
          if (window.personalised?.debugMode) console.debug('[SessionManager] Session freshness flag detected, clearing flag and forcing refresh');
          sessionStorage.removeItem('personalised_session_fresh');
          // Continue to fetch a fresh session by not returning early
        }
        // Use cached session if valid and not forcing refresh and no freshness flag
        else if (!forceRefresh && isValidSession(currentSession)) {
          if (window.personalised?.debugMode) console.debug('[SessionManager] Returning cached session');
          return currentSession;
        }

        // Avoid duplicate in-progress session fetches
        if (_sessionPromise && !forceRefresh) {
          if (window.personalised?.debugMode) console.debug('[SessionManager] Returning in-progress session promise');
          return _sessionPromise;
        }

        if (window.personalised?.debugMode) console.debug('[SessionManager] Starting new session fetch');

        // Start new session fetch
        _sessionPromise = (async () => {
          try {
            // Wait for Recharge SDK to be ready
            if (!window.rechargeInitialized && !window.recharge?.auth?.loginShopifyAppProxy) {
              if (window.rechargeInitializing) {
                if (window.personalised?.debugMode) console.debug('[SessionManager] Waiting for Recharge SDK to initialize...');
                await new Promise((resolve, reject) => {
                  const timeoutId = setTimeout(() => reject(new Error('Recharge SDK init timeout')), 5000);
                  window.addEventListener('recharge:ready', () => {
                    clearTimeout(timeoutId);
                    resolve();
                  }, { once: true });
                });
              } else {
                console.error('[SessionManager] SDK not initializing and not ready');
                return null;
              }
            }

            // Ensure SDK method is now available
            if (!window.recharge?.auth?.loginShopifyAppProxy) {
              console.error('[SessionManager] Recharge SDK still unavailable');
              return null;
            }

            // Try to get session via loginShopifyAppProxy (SDK v1.38.0)
            const session = await window.recharge.auth.loginShopifyAppProxy();
            if (isValidSession(session)) {
              currentSession = session;
              if (window.personalised?.debugMode) console.debug('[SessionManager] Session obtained and cached');
            } else {
              if (window.personalised?.debugMode) console.warn('[SessionManager] Invalid session from loginShopifyAppProxy');
            }

            return currentSession;
          } catch (error) {
            console.error('[SessionManager] Failed to retrieve session:', error);
            return null;
          } finally {
            // Clear the promise reference once completed
            _sessionPromise = null;
          }
        })();
        
        return _sessionPromise;
      },
      
      /**
       * Updates the current session
       * @param {Object} session - New session object 
       * @returns {boolean} Success status
       */
      updateSession(session) {
        if (!isValidSession(session)) {
          if (window.personalised?.debugMode) console.warn('[SessionManager] Attempted to update with invalid session');
          return false;
        }
        
        currentSession = session;
        if (window.personalised?.debugMode) console.debug('[SessionManager] Session updated successfully');
        return true;
      },
      
      /**
       * Checks if we have a valid session
       * @returns {boolean} True if the current session is valid
       */
      hasValidSession() {
        return isValidSession(currentSession);
      },
      
      /**
       * Checks if the session has authentication (customerId)
       * @returns {boolean} True if the session has a customerId
       */
      isAuthenticatedSession() {
        return isValidSession(currentSession) && !!currentSession.customerId;
      },
      
      /**
       * Gets customer ID from session if available
       * @returns {string|null} The customer ID or null
       */
      getCustomerId() {
        return this.isAuthenticatedSession() ? currentSession.customerId : null;
      }
    };
  })();
  
  // Register sessionManager in the personalised namespace
  window.personalised.sessionManager = sessionManager;

  /**
   * Formats an ID for Recharge external ID fields
   * @param {string|number} id - The ID to format
   * @returns {Object} Formatted ID object for Recharge
   */
  function formatExternalId(id) {
    return {
      ecommerce: String(id)
    };
  }

  /**
   * Formats the bundle items for Recharge's updateBundleSelection API call.
   * Uses a static collection_id and collection_source defined in config,
   * corresponding to the "Personalised" Recharge bundle collection.
   * 
   * @param {Array} items - The bundle items to format
   * @returns {Array} Formatted items for Recharge
   */
  function formatBundleItems(items) {
    // Pull config values safely
    const collectionId = window.personalised?.config?.bundle?.RECHARGE?.COLLECTION_ID || '512297271595';
    const collectionSource = window.personalised?.config?.bundle?.RECHARGE?.COLLECTION_SOURCE || 'shopify';
    
    return items
      .filter(item => item.product_id && item.variant_id)
      .map(item => ({
        external_product_id: String(item.product_id),
        external_variant_id: String(item.variant_id),
        collection_id: collectionId,
        collection_source: collectionSource,
        quantity: item.quantity
      }));
  }

  /**
   * Gets the current subscription details from metadata storage
   * This function provides consistent access to subscription data
   * 
   * @returns {Object|null} Subscription details object or null if not available
   */
  function getSubscriptionDetails() {
    // Get subscription details from metadata storage
    const storageManager = window.personalised?.storageManager;
    if (storageManager) {
      const storedDetails = storageManager.getMetadata('subscriptionDetails', null);
      if (storedDetails && typeof storedDetails === 'object') {
        // Add timestamp if missing (to track data freshness)
        if (!storedDetails.lastFetched) {
          storedDetails.lastFetched = Date.now();
        }
        return storedDetails;
      }
    }
    
    // Nothing found
    if (window.personalised?.debugMode) console.debug('[Utils] No subscription details found in metadata storage');
    return null;
  }

  /**
   * Extracts the essential subscription data fields needed for business logic
   * This helps standardize the subscription data format during the migration phase
   * 
   * @param {Object} fullSubscription - The complete subscription data object from Recharge
   * @returns {Object} Simplified subscription data with only essential fields
   */
  function extractEssentialSubscriptionData(fullSubscription) {
    if (!fullSubscription || typeof fullSubscription !== 'object') {
      if (window.personalised?.debugMode) console.warn('[Utils] Invalid subscription data provided to extractEssentialSubscriptionData');
      return null;
    }
    
    return {
      // Convert id to string to match metadata schema expectations
      id: fullSubscription.id ? String(fullSubscription.id) : null,
      status: fullSubscription.status || 'UNKNOWN',
      order_interval_frequency: fullSubscription.order_interval_frequency || null,
      purchaseItemId: fullSubscription.purchaseItemId || null,
      bundleSelectionId: fullSubscription.bundleSelectionId || null,
      lastFetched: Date.now() // Add timestamp to track data freshness
    };
  }

  /**
   * Refreshes subscription details from Recharge API and stores them in metadata
   * Uses the extractEssentialSubscriptionData helper to standardize the data format
   * 
   * @param {string} subscriptionId - The Recharge subscription ID to fetch
   * @returns {Promise<Object|null>} The refreshed subscription data or null if fetch failed
   */
  async function refreshSubscriptionDetails(subscriptionId) {
    if (!subscriptionId) {
      if (window.personalised?.debugMode) console.warn('[Utils] No subscription ID provided to refreshSubscriptionDetails');
      return null;
    }
    
    // Ensure rechargeUtils is available
    if (typeof window.personalised?.rechargeUtils?.getSubscription !== 'function') {
      if (window.personalised?.debugMode) console.warn('[Utils] rechargeUtils.getSubscription not available');
      return null;
    }
    
    try {
      // Fetch subscription details from Recharge
      const fullSubscription = await window.personalised.rechargeUtils.getSubscription(subscriptionId);
      if (!fullSubscription || typeof fullSubscription !== 'object') {
        if (window.personalised?.debugMode) console.warn('[Utils] Failed to fetch subscription details from Recharge');
        return null;
      }
      
      // Extract essential data
      const essentialData = extractEssentialSubscriptionData(fullSubscription);
      if (!essentialData) {
        return null;
      }
      
      // Store in metadata (if storageManager is available)
      const storageManager = window.personalised?.storageManager;
      if (storageManager && typeof storageManager.setMetadata === 'function') {
        storageManager.setMetadata('subscriptionDetails', essentialData);
      }
      
      // Return the essential data
      return essentialData;
    } catch (err) {
      console.error('[Utils] Error refreshing subscription details:', err);
      return null;
    }
  }

  // Initialize format utils in namespace early
  window.personalised.utils = window.personalised.utils || {};
  window.personalised.utils.formatExternalId = formatExternalId;
  window.personalised.utils.formatBundleItems = formatBundleItems;
  window.personalised.utils.getSubscriptionDetails = getSubscriptionDetails;
  window.personalised.utils.extractEssentialSubscriptionData = extractEssentialSubscriptionData;
  window.personalised.utils.refreshSubscriptionDetails = refreshSubscriptionDetails;

  /**
   * Initializes the user's interaction mode ("guest" or "edit").
   * Guarantees safe, memoized, idempotent async initialization.
   * This is the FULL implementation that will replace the placeholder.
   *
   * @returns {Promise<'guest' | 'edit'>} The resolved user mode.
   * 
   * Should be awaited before accessing any mode-dependent logic.
   */
  // Define fullInitMode in the global scope to ensure it's accessible when needed for replacement
  const fullInitMode = async function() {
    if (window.personalised.debugMode) console.debug('[initMode] Called - checking current mode');
    
    // Check if we already have a mode in storage
    if (window.personalised.storageManager.hasStoredMode()) {
      const currentMode = window.personalised.utils.getMode(); // Use the function from utils
      if (window.personalised.debugMode) console.debug(`[initMode] Mode found in storage: ${currentMode}`);
      return currentMode;
    }
    
    if (window.personalised.debugMode) console.debug('[initMode] No stored mode found, detecting mode...');
    
    // Check authentication status
    const isAuthenticated = window.personalised.sessionManager?.isAuthenticatedSession() || 
                          !!window.personalised.customerIdManager.get() ||
                          (document.body?.dataset?.customerLoggedIn === 'true');
    
    if (!isAuthenticated) {
      // No authentication = guest mode
      if (window.personalised.debugMode) console.info('[Mode] Setting mode to guest (no authentication)');
      window.personalised.utils.setCurrentMode('guest', { source: 'ensureModeInitialized:noAuth' });
      return 'guest';
    }
    
    // We have an authenticated user, check for subscription
    try {
      if (typeof window.personalised.rechargeUtils?.findPersonalisedPackSubscription === 'function') {
        const pack = await window.personalised.rechargeUtils.findPersonalisedPackSubscription();
        
        if (pack) {              // Subscription found - edit mode
            if (window.personalised.debugMode) console.info(`[Mode] Setting mode to edit with subscription ID: ${pack.id}`);
            window.personalised.utils.setCurrentMode('edit', { 
              source: 'ensureModeInitialized:subscription', 
              subscriptionId: pack.id
            });
          return 'edit';
        } else {
          // No subscription - new mode
          if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (authenticated without subscription)');
          window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:noSubscription' });
          return 'new';
        }
      } else {
        // Subscription check not available
        if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (subscription check unavailable)');
        window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:noSubCheck' });
        return 'new';
      }
    } catch (error) {
      if (window.personalised.debugMode) console.warn('[Mode] Error checking subscription status:', error);
      // Error but authenticated = new mode
      if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (error during subscription check)');
      window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:error' });
      return 'new';
    }
  };
  
  // Utilities implementation
  const utils = (function() {
    // Constant for maximum mode detection time (in milliseconds)
    const MODE_DETECTION_MAX_TIME = 30000; // 30 seconds
    
    // Timeout reference for mode detection safety
    let _modeDetectionTimeout = null;

    // Debounce function to limit how frequently operations can fire
    function debounce(func, wait = 800, immediate = false) {
      let timeout;
      return function(...args) {
        const context = this;
        const later = function() {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    }
  
    // Simple sync state tracking
    const syncState = {
      syncInProgress: false
    };
    
    // Mark sync as started
    function startSync() {
      syncState.syncInProgress = true;
      console.time('bundleSync');
    }
    
    // Mark sync as completed
    function completeSync() {
      syncState.syncInProgress = false;
      console.timeEnd('bundleSync');
    }
    
    /**
     * Get the current bundle mode based on multiple sources with persistence
     * 
     * Priority order:
     * 1. URL parameters (mode=edit with subscription_id)
     * 2. Session storage (if previously set by this function)
     * 3. Customer state (logged in = NEW, otherwise GUEST)
     * 
     * When in EDIT mode, saves mode and subscription_id to sessionStorage for persistence across pages
     * 
     * @returns {'GUEST'|'NEW'|'EDIT'} The determined mode
     */
    const VALID_MODES = ['edit', 'new', 'guest'];
    
    /**
     * Gets the current mode from session storage with fallbacks
     * @returns {string} The current mode ('edit', 'new', or 'guest')
     */
    function getMode() {
      // Using storageManager to get mode
      const mode = window.personalised.storageManager.getMode();
      
      if (window.personalised?.config?.debugMode) {
        console.debug(`[Mode Getter] Returning mode from storageManager: '${mode}'`);
      }
      
      return mode;
    }
    
    function getCurrentSubscriptionId() {
      // Using storageManager to get subscription ID
      return window.personalised.storageManager.getModeSubscriptionId();
    }

    /**
     * Sets the current mode and persists it to session storage via storageManager
     * The mode is now solely managed by the storageManager and no longer stored as a global variable
     * 
     * @param {string} mode - The mode to set ('edit', 'new', or 'guest')
     * @param {Object} options - Additional options
     * @param {string} [options.source] - Source of the mode change for debugging
     * @param {string|null} [options.subscriptionId] - Associated subscription ID if in edit mode
     */
    function setCurrentMode(mode, options = {}) {
      const normalised = String(mode).toLowerCase();
      const source = options.source || 'unknown';
      const subscriptionId = options.subscriptionId || null;

      if (!VALID_MODES.includes(normalised)) {
        console.warn(`[Mode Setter] Invalid mode '${mode}' – defaulting to guest`);
        mode = 'guest';
      }

      const data = {
        mode: normalised,
        timestamp: Date.now(),
        source,
        ...(subscriptionId && { subscriptionId }),
      };

      try {
        // Using storageManager to set mode
        window.personalised.storageManager.setMode(normalised, { source, subscriptionId });
        
        // Log mode changes with context
        if (window.personalised?.config?.debugMode) {
          console.debug(`[Mode Setter] Mode set to '${normalised}' via ${source}`, { 
            mode: normalised, 
            previousMode: window.personalised._previousMode || 'unknown',
            source,
            subscriptionId: subscriptionId || 'none',
            timestamp: new Date().toISOString()
          });
        }
        
        // Always log mode transitions since they're critical for debugging
        console.info(`[Mode Transition] Mode set to '${normalised}' with subscription ID: ${subscriptionId || 'none'} (source: ${source})`);
        
        // Track previous mode for debugging
        window.personalised._previousMode = normalised;
      } catch (e) {
        console.warn('[Mode Setter] Failed to access sessionStorage:', e);
      }
    }

    // Public API
    return {
      debounce,
      syncState,
      startSync,
      completeSync,
      getMode,
      getCurrentSubscriptionId,
      setCurrentMode,
      // We'll assign the correct implementation to window.personalised.utils.initMode later
      // Don't add a reference here to avoid circular reference issues
      // trackLoginState removed - now using personalisedCore.loginTracking
      validateMetadataIntegrity,
      handleAuthError
    };
  })();
  
  // Event Bus implementation (merged from personalised-event-bus.js)
  const eventBus = (function() {
    // Create a dedicated DOM element to act as our event target
    const eventTarget = document.createElement('div');
    eventTarget.id = 'personalised-event-target';
    
    /**
     * Registers an event listener for personalised events
     * @param {string} event - The event name to listen for
     * @param {function} callback - The callback to execute when the event is triggered
     */
    function on(event, callback) {
      const wrappedCallback = (e) => {
        try {
          callback(e.detail);
        } catch (err) {
          console.error(`Error in event '${event}' callback:`, err);
          // Error is caught and logged but doesn't break the event chain
        }
      };
      
      // Store the original callback reference for potential removal
      if (!callback._wrappedFn) {
        callback._wrappedFn = wrappedCallback;
      }
      
      eventTarget.addEventListener(event, wrappedCallback);
    }
    
    /**
     * Removes an event listener previously registered with on()
     * @param {string} event - The event name
     * @param {function} callback - The callback to remove
     */
    function off(event, callback) {
      // Remove the wrapper function that was created in on()
      if (callback && callback._wrappedFn) {
        eventTarget.removeEventListener(event, callback._wrappedFn);
        delete callback._wrappedFn;
      }
    }
    
    /**
     * Dispatches a personalised event with optional data
     * @param {string} event - The event name to dispatch
     * @param {any} data - The data to include with the event
     */
    function emit(event, data) {
      const customEvent = new CustomEvent(event, {
        bubbles: false,
        cancelable: true,
        detail: data
      });
      
      eventTarget.dispatchEvent(customEvent);
    }
    
    // Public API
    return {
      on,
      off,
      emit
    };
  })();
  
  // Recharge Utils implementation
  const rechargeUtils = (function() {
    /**
     * Check if Recharge SDK is available and properly loaded
     * @returns {boolean} Whether SDK is available and ready for use
     */
    function isRechargeSDKAvailable() {
      return typeof window.recharge !== 'undefined' && !!window.recharge;
    }

    // [Removed] Dynamic SDK loading replaced by global initialization in theme.liquid (v1.38.0)
    
    /**
     * Initialize Recharge SDK with storefront token
     * @returns {Promise<boolean>} Whether initialization succeeded
     */
    async function initRechargeSDK() {
      try {
        // SDK already initialized — skip
        if (window.rechargeInitialized) {
          console.debug('[Recharge] SDK already initialized globally');
          return true;
        }

        // SDK is initializing — wait for 'recharge:ready' with timeout
        if (window.rechargeInitializing) {
          console.debug('[Recharge] Waiting for SDK to finish initializing...');
          try {
            await new Promise((resolve, reject) => {
              const timeoutId = setTimeout(() => reject(new Error('Timed out waiting for Recharge SDK')), 5000);
              window.addEventListener('recharge:ready', () => {
                clearTimeout(timeoutId);
                resolve();
              }, { once: true });
            });
            return true;
          } catch (error) {
            console.error('[Recharge] Timeout or failure while waiting for SDK:', error);
            return false;
          }
        }

        // If SDK available but not initialized, fallback to local initialization
        if (window.recharge && window.recharge.init) {
          console.debug('[Recharge] Initializing SDK locally (fallback)');
          try {
            await window.recharge.init({
              storeIdentifier: window.Shopify?.shop || document.location.hostname,
              storefrontAccessToken: 'strfnt_4d357a0f69f38144cc1c3c548ea225c9229c04507b307b4f84cbcfc7c05eba80',
              loginRetryFn: async () => {
                try {
                  const session = await window.recharge.auth.loginShopifyAppProxy();
                  if (session) {
                    window.personalised?.sessionManager?.updateSession?.(session);
                  }
                  return session;
                } catch (error) {
                  console.error('[Recharge] Fallback loginRetryFn error:', error);
                  return null;
                }
              }
            });
            window.rechargeInitialized = true;
            window.dispatchEvent(new Event('recharge:ready'));
            return true;
          } catch (error) {
            console.error('[Recharge] Local fallback init failed:', error);
            return false;
          }
        }

        console.error('[Recharge] SDK not available');
        return false;
      } catch (err) {
        console.error('Failed to initialize Recharge SDK:', err);
        return false;
      }
    }
    


    /**
     * Fetch all subscriptions for authenticated customer
     * @returns {Promise<Array>} Array of subscription objects or empty array if error/none found
     */
    /**
     * Fetches all subscriptions for the current customer from Recharge API
     * Handles different response formats from various Recharge API versions
     * @returns {Promise<Array>} Array of subscription objects or empty array if none found
     */
    async function fetchSubscriptions() {
      try {
        if (window.personalised?.config?.debugMode) {
          console.debug('[fetchSubscriptions] Starting fetch from Recharge');
        }
        
        // Defensive guard clause for sessionManager
        if (typeof window.personalised?.sessionManager?.getSession !== 'function') {
          console.error('[fetchSubscriptions] Session manager not initialized or getSession not available');
          return [];
        }
        
        // Verify SDK is available
        if (!isRechargeSDKAvailable()) {
          console.error('Recharge SDK not available for subscription fetch');
          return [];
        }
        
        // Get session from session manager - this is the authoritative source for customer ID
        const session = await window.personalised.sessionManager.getSession();
        
        console.debug('[Fix] Recharge session for subscription fetch:', {
          sessionExists: !!session,
          hasCustomerId: window.personalised.sessionManager.hasValidSession() && !!session?.customerId,
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available',
          windowCustomerId: window.personalised.customerIdManager.get() || 'not available',
          shopifyCustomerId: Shopify?.customer?.id || 'not available'
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          console.error('[Fix] No valid Recharge session available for subscription fetch');
          return [];
        }
        
        if (!window.personalised.sessionManager.getCustomerId()) {
          console.error('[Fix] Recharge session has no customerId, cannot fetch subscriptions');
          return [];
        }
        
        // Declare response variable outside try block to maintain scope
        let response;
        
        try {
          // Ensure Recharge SDK is ready
          await window.personalised.utils.waitForRechargeSDK();
          
          // Ensure Recharge SDK subscription API is available
          if (!window.recharge?.subscription?.listSubscriptions) {
            console.error('Recharge SDK subscription.listSubscriptions API not available');
            return [];
          }

          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] About to call Recharge API', {
              sessionValid: !!session,
              sessionCustomerId: session.customerId,
              apiEndpoint: 'subscription.listSubscriptions',
              sdkVersion: window.recharge?.version || 'unknown'
            });
          }
          
          // Get all customer's subscriptions using the proper API method
          console.debug('[Fix] Calling recharge.subscription.listSubscriptions with session:', {
            apiToken: session.apiToken ? 'present' : 'missing',
            customerId: session.customerId || 'not available'
          });
          
          response = await window.recharge.subscription.listSubscriptions(session);
        } catch (sdkError) {
          console.error('Error waiting for Recharge SDK:', sdkError);
          return [];
        }
        console.log('[Recharge] Subscriptions:', response);
        
        if (window.personalised?.config?.debugMode) {
          console.debug('[fetchSubscriptions] Raw response received', {
            responseType: typeof response,
            isArray: Array.isArray(response),
            hasSubscriptionsProperty: response && typeof response === 'object' && 'subscriptions' in response,
            hasNestedData: response && typeof response === 'object' && response.data && 'subscriptions' in response.data,
            responseKeys: response && typeof response === 'object' ? Object.keys(response) : []
          });
        }
        
        // Enhanced response validation to handle multiple Recharge API response formats
        let subscriptions = [];
        let formatDetected = 'unknown';
        
        // Format detection and parsing with robust fallbacks
        if (Array.isArray(response)) {
          // Format 1: Direct array response format
          subscriptions = response;
          formatDetected = 'direct-array';
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] Parsed direct array response format', { 
              count: subscriptions.length,
              sample: subscriptions.length > 0 ? { id: subscriptions[0].id } : 'none' 
            });
          }
        } else if (response && Array.isArray(response.subscriptions)) {
          // Format 2: Object with subscriptions array property
          subscriptions = response.subscriptions;
          formatDetected = 'wrapped-subscriptions';
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] Parsed response.subscriptions format', { 
              count: subscriptions.length,
              sample: subscriptions.length > 0 ? { id: subscriptions[0].id } : 'none'
            });
          }
        } else if (response && response.data && Array.isArray(response.data.subscriptions)) {
          // Format 3: Nested data.subscriptions format (GraphQL-style)
          subscriptions = response.data.subscriptions;
          formatDetected = 'data-subscriptions';
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] Parsed response.data.subscriptions format', { 
              count: subscriptions.length,
              sample: subscriptions.length > 0 ? { id: subscriptions[0].id } : 'none'
            });
          }
        } else {
          // Unable to parse any known format - provide detailed warning
          console.warn('[fetchSubscriptions] Invalid subscriptions response format from Recharge:', {
            responseType: typeof response,
            hasData: response && typeof response.data === 'object',
            keys: response ? Object.keys(response) : [],
            payload: typeof response === 'object' ? JSON.stringify(response) : typeof response
          });
          return [];
        }
        
        // Safety check for empty array
        if (subscriptions.length === 0) {
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] No subscriptions found for customer');
          }
        } else if (window.personalised?.config?.debugMode) {
          console.debug(`[fetchSubscriptions] Successfully parsed ${subscriptions.length} subscriptions using format: ${formatDetected}`);
        }
        
        return subscriptions;
      } catch (err) {
        console.error('Failed to fetch Recharge subscriptions:', err);
        return [];
      }
    }

    /**
     * Find the customer's personalised pack subscription if it exists
     * @returns {Promise<Object|null>}
     */
    /**
     * Find the customer's personalised pack subscription if it exists
     * Uses multiple matching strategies: handle and title with locale-aware spelling
     * @returns {Promise<Object|null>} The subscription object or null if not found
     */
    /**
     * Find a personalised pack subscription for the current customer
     * Uses multiple matching strategies with fallbacks to identify the correct subscription
     * @returns {Promise<Object|null>} Subscription object or null if not found
     */
    async function findPersonalisedPackSubscription() {
      try {
        console.debug('[Subscription Search] Starting personalised pack subscription search');
        console.debug('[Subscription Search] Customer context:', {
          customerId: window.personalised.customerIdManager.get() || 'not available',
          customerEmail: 'removed - debug only',
          hasValidSession: window.personalised.sessionManager.hasValidSession(),
          isAuthenticatedSession: window.personalised.sessionManager.isAuthenticatedSession(),
          rechargeSessionCustomerId: window.personalised.sessionManager.getCustomerId() || 'not available'
        });
        
        console.debug('[Subscription Search] About to call fetchSubscriptions()');
        
        const subscriptions = await fetchSubscriptions();
        
        // Enhanced detailed logging with explicit subscription detection
        console.log('🔍 [Subscription Debug] Full subscription data retrieved:', subscriptions);
        
        if (!subscriptions || subscriptions.length === 0) {
          console.log('⚠️ [Subscription Debug] No subscriptions found for customer');
          return null;
        }
        
        // Log detailed information about each subscription for diagnosis
        console.log(`🔄 [Subscription Debug] Found ${subscriptions.length} subscriptions:`);
        
        subscriptions.forEach((sub, i) => {
          // Extract all relevant fields that might help with matching
          const {
            id,
            status,
            sku,
            created_at,
            next_charge_scheduled_at,
            order_interval_frequency,
            external_product_id,
            external_variant_id,
            product_title
          } = sub;
          
          // Also check if these fields exist in product sub-object (Recharge API inconsistency)
          const product = sub.product || {};
          
          // Log comprehensive subscription details
          console.log(`📦 [Subscription ${i+1}/${subscriptions.length}] ID: ${id}`, {
            // Basic subscription info
            id,
            status,
            sku: sku || 'not available',
            created_at: created_at || 'not available',
            next_charge_scheduled_at: next_charge_scheduled_at || 'not available',
            frequency: order_interval_frequency || 'not available',
            
            // Direct subscription product fields
            external_product_id: external_product_id || 'not available',
            external_variant_id: external_variant_id || 'not available',
            product_title: product_title || 'not available',
            
            // Product sub-object fields
            product_id: product.id || 'not available',
            product_handle: product.handle || 'not available',
            product_title_from_object: product.title || 'not available',
            
            // Additional helpful fields
            tags: product.tags || 'not available',
            vendor: product.vendor || 'not available',
            shopify_product_id: product.shopify_product_id || external_product_id || 'not available',
            sku_from_product: product.sku || 'not available'
          });
        });
        
        if (!subscriptions || subscriptions.length === 0) {
          if (window.personalised?.config?.debugMode) {
            console.debug('[findPersonalisedPackSubscription] No subscriptions to search');
          }
          return null;
        }
        
        // Enhanced matching with detailed logging
        console.log('🧪 [Subscription Debug] Starting personalised pack detection with multiple strategies');
        
        // Track which matching strategy was used (for diagnosis)
        let matchStrategy = 'none';
        
        // Define known personalized pack identifiers
        const BUNDLE_MATCH_PATTERNS = {
          // Product IDs from config
          configuredIds: window.personalised?.config?.bundle?.KNOWN_PRODUCT_IDS || [],
          
          // Default list of known product IDs if not in config - can be expanded
          fallbackProductIds: ['9972063732011', '6948805869631'],
          
          // Common handles for personalized packs
          handles: [
            'personalised-pack', 
            'personalised-bundle' 
          ],
          
          // Title patterns for fuzzy matching
          titlePatterns: [
            'personalised pack',
            'personalised bundle'
          ],
          
          // SKU patterns that might indicate personalized products
          skuPatterns: [
            'pers-pack',
            'pers_pack',
            'personalised',
            'personalized',
            'custom-bundle'
          ]
        };
        
        console.log('🔍 [Subscription Debug] Using match patterns:', BUNDLE_MATCH_PATTERNS);
        
        // Check if we have product IDs configured for matching
        const knownProductIds = BUNDLE_MATCH_PATTERNS.configuredIds;
        const hasProductIdConfig = Array.isArray(knownProductIds) && knownProductIds.length > 0;
        
        if (!hasProductIdConfig) {
          console.log('⚠️ [Subscription Debug] No KNOWN_PRODUCT_IDS in config, will use fallback IDs and other match strategies');
        }
        
        // Track match candidates with scores for better decision making
        const matchCandidates = [];
        
        // Enhanced search using multiple patterns and fallback strategies
        subscriptions.forEach((sub, index) => {
          // Safety check for malformed subscription data
          if (!sub || typeof sub !== 'object') {
            console.log(`❌ [Subscription ${index+1}] Invalid subscription object`);
            return;
          }
          
          let matchScore = 0;
          const matchReasons = [];
          
          // Get product info from various possible locations in the subscription object
          const product = sub.product || {};
          
          // Helper function to extract product IDs from potentially nested objects
          function extractProductId(idField) {
            if (!idField) return '';
            
            // If it's a string already, return it
            if (typeof idField === 'string') return idField;
            
            // If it's an object with ecommerce property (Recharge API format)
            if (typeof idField === 'object' && idField.ecommerce) {
              return String(idField.ecommerce);
            }
            
            // Fall back to string conversion
            return String(idField);
          }
          
          // Normalize all potential ID fields for comparison
          const productIds = [
            extractProductId(product.id),
            extractProductId(product.shopify_product_id),
            extractProductId(sub.external_product_id),
            extractProductId(product.external_product_id)
          ].filter(id => id && id !== 'undefined' && id !== 'null');
          
          console.log(`🔍 [Subscription ${index+1}] Checking ID match:`, {
            subscriptionId: sub.id,
            productIds,
            knownIds: knownProductIds,
            fallbackIds: BUNDLE_MATCH_PATTERNS.fallbackProductIds
          });
          
          // Strategy 1: Match by product ID (highest priority)
          if (productIds.length > 0) {
            // Check against configured product IDs
            if (hasProductIdConfig) {
              const configMatch = productIds.some(id => knownProductIds.includes(id));
              if (configMatch) {
                matchScore += 100; // Highest confidence
                matchReasons.push('configured-product-id');
                console.log(`✅ [Subscription ${index+1}] Matched by configured product ID`);
              }
            }
            
            // Check against fallback product IDs
            const fallbackMatch = productIds.some(id => 
              BUNDLE_MATCH_PATTERNS.fallbackProductIds.includes(id));
            if (fallbackMatch) {
              matchScore += 90; // Very high confidence
              matchReasons.push('fallback-product-id');
              console.log(`✅ [Subscription ${index+1}] Matched by fallback product ID`);
            }
          }
          
          // Strategy 2: Check product handle
          const handle = String(product.handle || '').toLowerCase();
          if (handle) {
            const handleMatch = BUNDLE_MATCH_PATTERNS.handles.some(pattern => 
              handle.includes(pattern));
            if (handleMatch) {
              matchScore += 80; // High confidence
              matchReasons.push('handle');
              console.log(`✅ [Subscription ${index+1}] Matched by handle: "${handle}"`);
            }
          }
          
          // Strategy 3: Check product title (multiple sources)
          const titles = [
            String(product.title || '').toLowerCase(),
            String(sub.product_title || '').toLowerCase()
          ].filter(title => title);
          
          if (titles.length > 0) {
            const titleMatches = titles.some(title => 
              BUNDLE_MATCH_PATTERNS.titlePatterns.some(pattern => 
                title.includes(pattern)));
            
            if (titleMatches) {
              matchScore += 70; // Good confidence
              matchReasons.push('title');
              console.log(`✅ [Subscription ${index+1}] Matched by title: "${titles.join(', ')}"`);
            }
          }
          
          // Strategy 4: Check SKUs (multiple sources)
          const skus = [
            String(sub.sku || '').toLowerCase(),
            String(product.sku || '').toLowerCase()
          ].filter(sku => sku);
          
          if (skus.length > 0) {
            const skuMatches = skus.some(sku => 
              BUNDLE_MATCH_PATTERNS.skuPatterns.some(pattern => 
                sku.includes(pattern)));
            
            if (skuMatches) {
              matchScore += 60; // Medium confidence
              matchReasons.push('sku');
              console.log(`✅ [Subscription ${index+1}] Matched by SKU: "${skus.join(', ')}"`);
            }
          }
          
          // Strategy 5: Check tags if available
          if (product.tags && typeof product.tags === 'string') {
            const tags = product.tags.toLowerCase();
            if (tags.includes('personalised') || tags.includes('personalized') || 
                tags.includes('bundle') || tags.includes('subscription')) {
              matchScore += 50; // Lower confidence
              matchReasons.push('tags');
              console.log(`✅ [Subscription ${index+1}] Matched by tags: "${tags}"`);
            }
          }
          
          // Add to candidates if we have any match
          if (matchScore > 0) {
            matchCandidates.push({
              subscription: sub,
              score: matchScore,
              reasons: matchReasons,
              index
            });
          } else {
            console.log(`❌ [Subscription ${index+1}] No match found for this subscription`);
          }
        });
        
        console.log(`🧮 [Subscription Debug] Found ${matchCandidates.length} potential matches`, matchCandidates);
        
        // Select the best match based on score
        matchCandidates.sort((a, b) => {
          const sa = ((a.subscription?.status || '') + '').toUpperCase() === 'ACTIVE' ? 1 : 0;
          const sb = ((b.subscription?.status || '') + '').toUpperCase() === 'ACTIVE' ? 1 : 0;
          if (sb - sa !== 0) return sb - sa;        // ACTIVE first
          return b.score - a.score;                 // keep existing behavior
        });
        
        // Get the best match if available
        const bestMatch = matchCandidates.length > 0 ? matchCandidates[0] : null;
        const foundSubscription = bestMatch ? bestMatch.subscription : null;
        
        if (bestMatch) {
          matchStrategy = bestMatch.reasons.join('+');
          console.log(`✅ [Subscription Debug] Best match found:`, {
            subscriptionId: bestMatch.subscription.id,
            score: bestMatch.score,
            matchStrategy: matchStrategy,
            matchReasons: bestMatch.reasons
          });
        } else {
          console.log(`❌ [Subscription Debug] No personalized pack subscription found after trying all strategies`);
        }
        
        // Enhanced result logging with detailed information about the found subscription
        if (foundSubscription) {
          console.log(`✅ [Subscription Success] Found personalised pack subscription:`, {
            id: foundSubscription.id,
            status: foundSubscription.status,
            matchStrategy,
            productId: foundSubscription.product?.id || foundSubscription.external_product_id || 'unknown',
            title: foundSubscription.product?.title || foundSubscription.product_title || 'unknown',
            sku: foundSubscription.product?.sku || foundSubscription.sku || 'unknown',
            nextChargeDate: foundSubscription.next_charge_scheduled_at || 'unknown',
            frequency: foundSubscription.order_interval_frequency || 'unknown'
          });
          
          // Save last successful match strategy for future reference
          window.personalised._lastSuccessfulMatchStrategy = matchStrategy;
        } else {
          console.log(`❌ [Subscription Failure] No personalised pack subscription found`);
          console.log(`💡 [Subscription Tip] Consider adding your product ID to KNOWN_PRODUCT_IDS in config for more reliable detection`);
        }
        
        return foundSubscription;
      } catch (err) {
        console.error('❌ [Subscription Error] Failed to find personalised pack subscription:', err);
        return null;
      }
    }

    /**
     * Get bundle selection details for a specific subscription
     * @param {string|number} subscriptionId - Recharge subscription ID
     * @returns {Promise<Object|null>} Bundle data formatted for loadFromRecharge
     */
    async function getBundleSelectionForSubscription(subscriptionId) {
      try {
        // Verify SDK is available
        if (!isRechargeSDKAvailable()) {
          console.error('Recharge SDK not available for bundle selection fetch');
          return null;
        }
        
        // TODO: Even though this is a read operation, consider using getSession({ forceRefresh: true }) 
        // to prevent issues with stale tokens when reading sensitive customer data
        const session = await window.personalised.sessionManager.getSession();
        console.debug('[Fix] Bundle fetch session:', {
          sessionValid: window.personalised.sessionManager.hasValidSession(),
          sessionAuthenticated: window.personalised.sessionManager.isAuthenticatedSession(),
          hasApiToken: !!session?.apiToken,
          hasCustomerId: !!window.personalised.sessionManager.getCustomerId(),
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available'
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          console.error('[Fix] No valid Recharge session available for bundle fetch');
          return null;
        }
        
        if (!session.apiToken) {
          console.error('[Fix] No apiToken in session, cannot fetch bundle selections');
          return null;
        }

        const subId = Number(subscriptionId);
        if (isNaN(subId)) {
          console.warn(`Invalid subscription ID: ${subscriptionId}`);
          return null;
        }

        let subscription;
        let bundleSelections;

        try {
          // Ensure Recharge SDK is ready
          await window.personalised.utils.waitForRechargeSDK();
          
          if (!window.recharge?.subscription?.getSubscription || 
              !window.recharge?.bundle?.listBundleSelections) {
            console.error('Recharge SDK methods not available');
            return null;
          }

          // Get subscription first to verify it exists
          subscription = await window.recharge.subscription.getSubscription(session, subId);
          if (!subscription) {
            console.warn(`No subscription found for ID: ${subscriptionId}`);
            return null;
          }

          // Use listBundleSelections with purchase_item_ids
          console.debug('[Fix] Calling listBundleSelections for subscription ID:', subscription.id);
          bundleSelections = await window.recharge.bundle.listBundleSelections(session, {
            purchase_item_ids: [subscription.id],
            limit: 1
          });
        } catch (error) {
          console.error(`Error fetching bundle selection for subscription ${subId}:`, error);
          return null;
        }
        
        console.log('[Recharge] BundleSelections:', bundleSelections);

        if (!bundleSelections?.bundle_selections?.length) {
          console.warn(`[Fix] No bundle selections found for subscription: ${subscription.id}`);
          return null;
        }

        // Find the personalised pack bundle
        const bundleSelection = bundleSelections.bundle_selections.find(
          b => b.external_product_id === '9972063732011'
        );

        if (!bundleSelection) {
          console.warn(`No personalised pack bundle found for subscription: ${subscription.id}`);
          return null;
        }

        // Log critical objects for diagnostic purposes with clear identification of IDs
        const purchaseItemId = subscription.purchase_items?.[0]?.id || subscription.id;
        console.log('🔍 [DIAGNOSTIC] Bundle Fetch Objects:', {
          subscription: subscription,
          bundleSelection: bundleSelection,
          purchaseItemId: purchaseItemId,
          bundleSelectionId: bundleSelection.id, // This is critical for updateBundleSelection
          usingPurchaseItem: !!subscription.purchase_items?.[0]?.id
        });

        // Format bundle data for loadFromRecharge with enhanced metadata
        return {
          subscription: {
            id: subscription.id,
            order_interval_frequency: subscription.order_interval_frequency || '30',
            charge_interval_frequency: subscription.charge_interval_frequency || subscription.order_interval_frequency || '30',
            shipping_interval_unit_type: subscription.order_interval_unit || 'day',
            shipping_interval_frequency: subscription.order_interval_frequency || '30'
          },
          bundle: {
            id: bundleSelection.id,
            bundleRechargeId: bundleSelection.bundle_id || bundleSelection.bundle?.id || bundleSelection.id,
            external_product_id: bundleSelection.external_product_id,
            external_variant_id: bundleSelection.external_variant_id,
            purchaseItemId: purchaseItemId // Use purchase_item_id from subscription
          },
          items: bundleSelection.items.map(item => ({
            productId: item.external_product_id,
            variantId: item.external_variant_id,
            collectionId: item.collection_id || getCollectionIdFromProperties(item.properties),
            quantity: 1, // Reset to base quantity 1 – quantities from Recharge include frequency multiplier
            sellingPlanId: item.selling_plan_id || null
          })).filter(item => item.productId && item.variantId)
        };

      } catch (err) {
        console.error(`Error loading bundle for subscription ${subscriptionId}:`, err);
        return null;
      }
    }
    
    /**
     * Extract collection ID from line item properties
     * @param {Object} properties - Line item properties
     * @returns {string|null} Collection ID or null if not found
     */
    function getCollectionIdFromProperties(properties) {
      if (!properties) return null;
      const bundleKey = window.personalised?.config?.bundle?.RECHARGE_KEYS?.BUNDLE_PROPERTY || '_rc_bundle';
      return properties[bundleKey] || null;
    }
    
    /**
     * Updates the frequency of an existing subscription
     * @param {string|number} subscriptionId - The subscription ID to update
     * @param {string} frequency - The new frequency (30, 60, or 90)
     * @returns {Promise<{success: boolean, error: Error|null}>} Result object
     */
    async function updateSubscriptionFrequency(subscriptionId, frequency) {
      try {
        // Always force a fresh session for subscription updates to prevent 403 errors from stale tokens
        const session = await window.personalised.sessionManager.getSession({ forceRefresh: true });
        console.debug('[UpdateSubscriptionFrequency] Session validation (fresh):', {
          sessionValid: window.personalised.sessionManager.hasValidSession(),
          sessionAuthenticated: window.personalised.sessionManager.isAuthenticatedSession(),
          hasCustomerId: !!window.personalised.sessionManager.getCustomerId(),
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available',
          subscriptionId,
          frequency
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          return {
            success: false,
            error: new Error('No valid Recharge session available for frequency update')
          };
        }

        // Validate frequency
        const validFrequencies = ['30', '60', '90'];
        if (!validFrequencies.includes(String(frequency))) {
          return {
            success: false,
            error: new Error(`Invalid frequency: ${frequency}. Must be one of: ${validFrequencies.join(', ')}`)
          };
        }

        // Update subscription with new frequency
        await window.recharge.subscription.updateSubscription(session, subscriptionId, {
          charge_interval_frequency: frequency,
          order_interval_frequency: frequency,
          order_interval_unit: "day"
        });

        return { success: true, error: null };
      } catch (err) {
        console.error('Subscription frequency update failed:', err);
        return { success: false, error: err };
      }
    }

    /**
     * Updates an existing bundle selection with the current state
     * Uses the correct Recharge updateBundleSelection API method for customer's bundle selection
     * @returns {Promise<{success: boolean, error: Error|null}>} Result object
     */
    async function updateBundle() {
      try {
        // custom: personalised-bundle-refactor - enhanced metadata validation
        console.log('🔍 [Bundle Update] Validating critical metadata...');
        
        // Check for critical metadata in primary source first
        const hasMetadataInPrimary = {
          bundleSelectionId: !!window.personalised.bundle.getMetadata('bundleSelectionId'),
          subscriptionId: !!window.personalised.bundle.getMetadata('subscriptionId'),
          purchaseItemId: !!window.personalised.bundle.getMetadata('purchaseItemId')
        };
        
        // Log validation state
        console.log('[Bundle Update] Primary source metadata check:', hasMetadataInPrimary);

        // Attempt to get bundleSelectionId with fallbacks
        const bundleSelectionId = window.personalised.bundle.getMetadata('bundleSelectionId')
          || window.personalised.storageManager.getMetadata('bundleSelectionId');

        if (!hasMetadataInPrimary.bundleSelectionId && bundleSelectionId) {
          console.warn('⚠️ [Bundle Update] bundleSelectionId not found in primary bundle state, using fallback source');
        }

        if (!bundleSelectionId) {
          console.error('❌ [Bundle Update] Bundle Selection ID not found in any source. Cannot update.');
          return { 
            success: false, 
            error: new Error('Bundle Selection ID not found. Cannot update.') 
          };
        }

        // Attempt to get subscriptionId with fallbacks
        const subscriptionId = window.personalised.bundle.getMetadata('subscriptionId')
          || window.personalised.storageManager.getMetadata('subscriptionId');

        if (!hasMetadataInPrimary.subscriptionId && subscriptionId) {
          console.warn('⚠️ [Bundle Update] subscriptionId not found in primary bundle state, using fallback source');
        }
                                 
        if (!subscriptionId) {
          console.error('❌ [Bundle Update] Subscription ID not found in any source. Cannot update.');
          return { 
            success: false, 
            error: new Error('Subscription ID not found. Cannot update.') 
          };
        }
        
        // Check for Recharge SDK
        if (!isRechargeSDKAvailable()) {
          return { 
            success: false, 
            error: new Error('Recharge SDK not available') 
          };
        }
        
        // Use refreshSessionTokensOnly to reset session and tokens but preserve mode and subscription info
        // This prevents 403 errors from stale tokens after page reload or portal return
        if (window.personalised.utils?.refreshSessionTokensOnly) {
          window.personalised.utils.refreshSessionTokensOnly('update_bundle');
        }
        
        // Then get the fresh session
        const session = await window.personalised.sessionManager.getSession();
        
        console.debug('[UpdateBundle] Session validation (fresh):', {
          sessionValid: window.personalised.sessionManager.hasValidSession(),
          sessionAuthenticated: window.personalised.sessionManager.isAuthenticatedSession(),
          hasCustomerId: !!window.personalised.sessionManager.getCustomerId(),
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available'
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          return { 
            success: false, 
            error: new Error('No valid Recharge session available for bundle update') 
          };
        }
        
        // Get current state in Recharge format (now returns base quantities only)
        const currentState = window.personalised.bundle.toRechargeFormat(); // custom: migrated from legacy state
        
        // custom: personalised-bundle-refactor - enhanced with safe fallbacks
        // Get originalFrequency with fallbacks
        const originalFrequency = window.personalised.bundle.getMetadata('originalFrequency')
          || window.personalised.storageManager.getMetadata('originalFrequency');
        
        if (!window.personalised.bundle.getMetadata('originalFrequency') && originalFrequency) {
          console.warn('[Fallback] originalFrequency not found in bundleState, falling back to legacy sources');
        }
                                 
        // subscriptionId is already retrieved at the start of the function (custom: personalised-bundle-refactor)
        
        // Check if frequency has changed
        if (originalFrequency && originalFrequency !== window.personalised.bundle.getFrequency()) { // custom: migrated from legacy state
          const frequencyResult = await updateSubscriptionFrequency(
            subscriptionId,
            window.personalised.bundle.getFrequency() // custom: migrated from legacy state
          );
          
          if (!frequencyResult.success) {
            return frequencyResult;
          }
        }
        
        // Get base items from state and apply frequency multiplier
        const baseItems = window.personalised.bundle.getItems(); // custom: migrated from legacy state
        const multiplier = window.personalised.bundle.getFrequencyMultiplier(); // custom: migrated from legacy state
        
        // Do NOT apply frequency multiplier again - it's already applied in toRechargeFormat()
        // Using currentState items directly to avoid double multiplication
        
        // Format items using utility function
        const items = window.personalised.utils.formatBundleItems(currentState.items);
        
        if (items.length === 0) {
          console.error("❌ [ERROR] No valid items to update. Aborting bundle selection update.");
          return {
            success: false,
            error: new Error("No valid items to update bundle selection with.")
          };
        }
        
        // custom: personalised-bundle-refactor - enhanced metadata validation
        // Get purchaseItemId with fallbacks
        const purchaseItemId = window.personalised.bundle.getMetadata('purchaseItemId')
          || window.personalised.storageManager.getMetadata('purchaseItemId');

        if (!hasMetadataInPrimary.purchaseItemId && purchaseItemId) {
          console.warn('⚠️ [Bundle Update] purchaseItemId not found in primary bundle state, using fallback source');
        }

        // Log final validation state
        console.log('[Bundle Update] Final metadata validation:', {
          bundleSelectionId: !!bundleSelectionId,
          subscriptionId: !!subscriptionId,
          purchaseItemId: !!purchaseItemId
        });
                                 
        // Removed legacy warning - using bundle.getMetadata() (custom: personalised-bundle-refactor)
        
        // Prepare bundle selection payload according to Recharge API requirements
        // The only required fields for updateBundleSelection are purchase_item_id and items
        const bundleSelectionPayload = {
          purchase_item_id: purchaseItemId,
          items
        };
        
        // Log detailed update information for diagnostics
        console.log('🔍 [DIAGNOSTIC] Bundle Selection Update:', {
          bundleSelectionId: bundleSelectionId,
          purchaseItemId: purchaseItemId,
          itemCount: items.length,
          payload: bundleSelectionPayload
        });
        
        // Check for missing required IDs
        if (!bundleSelectionId) {
          console.error('❌ [ERROR] Missing bundleSelectionId. Cannot update bundle selection.');
          return { 
            success: false, 
            error: new Error('Bundle Selection ID is required for update.') 
          };
        }
        
        if (!purchaseItemId) {
          console.error('❌ [ERROR] Missing purchase_item_id. Cannot update bundle selection.');
          return { 
            success: false, 
            error: new Error('Purchase item ID is required for bundle selection update.') 
          };
        }
        
        console.log("🚀 Submitting bundle selection update:", { 
          bundleSelectionId,
          purchaseItemId,
          itemCount: items.length
        });
        
        // Debug log to verify collection_id in final payload
        console.log("[DEBUG] Final Payload for updateBundleSelection:", bundleSelectionPayload);
        
        // Log quantities to verify they are correct
        console.log('📊 [Bundle Debug] Quantities being sent to Recharge:', 
          items.map(item => `${item.external_product_id}: ${item.quantity}`));
        
        // Update the bundle selection using the proper method for customer's bundle selection
        await window.recharge.bundle.updateBundleSelection(
          session,
          bundleSelectionId,
          bundleSelectionPayload,
          { commit: true }
        );
        
        // Clear the session flag after successful Recharge update
        // This allows normal Recharge data loading on subsequent page loads
        // since the local changes have been successfully synced to Recharge
        // Using storageManager to clear modified flag
        window.personalised.storageManager.clearModifiedFlag();
        
        // Return success
        return { success: true, error: null };
      } catch (err) {
        console.error('❌ [ERROR] Bundle selection update failed:', err);
        return { success: false, error: err };
      }
    }

    // Public API
    return {
      initRechargeSDK,
      fetchSubscriptions,
      findPersonalisedPackSubscription,
      getBundleSelectionForSubscription,
      isRechargeSDKAvailable,
      formatExternalId,
      formatBundleItems,
      updateBundle,
      updateSubscriptionFrequency // Expose the new function
    };
  })();
  
  // Merge utils with existing namespace (don't overwrite)
  Object.assign(window.personalised.utils, utils);
  window.personalised.eventBus = eventBus;
  window.personalised.rechargeUtils = rechargeUtils; // Add rechargeUtils to namespace
  
  // 🔧 ID Normalization and Comparison Utilities
  /**
   * Our internal type standard for IDs:
   * -----------------------------
   * - Shopify IDs (variantId, productId, collectionId): 
   *   Store as NUMBER internally, convert to STRING only at API/DOM boundaries
   *   
   * - Recharge IDs (bundleSelectionId, purchaseItemId): 
   *   Always store and use as STRING
   * 
   * This standardization helps:
   * 1. Reduce brittle comparisons (=== vs == issues)
   * 2. Prevent runtime bugs from type mismatches 
   * 3. Ensure consistent behavior across the bundle system
   * 4. Make code intention clearer (which IDs are Shopify vs Recharge)
   */
  
  /**
   * Normalizes a Shopify ID to its standard internal format (number)
   * @param {string|number} id - The ID to normalize
   * @returns {number|null} The normalized ID as a number, or null if invalid
   */
  function normalizeShopifyId(id) {
    if (id === null || id === undefined) return null;
    const numId = Number(id);
    return !isNaN(numId) ? numId : null;
  }

  /**
   * Safely compares two Shopify IDs for equality
   * @param {string|number} id1 - First ID to compare
   * @param {string|number} id2 - Second ID to compare
   * @returns {boolean} True if IDs are equal, false otherwise
   */
  function shopifyIdsEqual(id1, id2) {
    const num1 = normalizeShopifyId(id1);
    const num2 = normalizeShopifyId(id2);
    return num1 !== null && num2 !== null && num1 === num2;
  }

  /**
   * Normalizes a Recharge ID to its standard internal format (string)
   * @param {string|number} id - The ID to normalize 
   * @returns {string|null} The normalized ID as a string, or null if invalid
   */
  function normalizeRechargeId(id) {
    if (id === null || id === undefined) return null;
    return String(id);
  }

  /**
   * Safely compares two Recharge IDs for equality
   * @param {string|number} id1 - First ID to compare
   * @param {string|number} id2 - Second ID to compare
   * @returns {boolean} True if IDs are equal, false otherwise
   */
  function rechargeIdsEqual(id1, id2) {
    const str1 = normalizeRechargeId(id1);
    const str2 = normalizeRechargeId(id2);
    return str1 !== null && str2 !== null && str1 === str2;
  }

  /**
   * Formats a Shopify ID for external API use (as string)
   * @param {string|number} id - The ID to format for API use
   * @returns {string|null} The ID formatted as a string, or null if invalid
   */
  function formatShopifyIdForApi(id) {
    const numId = normalizeShopifyId(id);
    return numId !== null ? String(numId) : null;
  }
  
  // Add the ID utility functions to the namespace
  const idUtils = {
    normalizeShopifyId,
    shopifyIdsEqual,
    normalizeRechargeId,
    rechargeIdsEqual,
    formatShopifyIdForApi
  };
  
  // Add to global namespace
  Object.assign(window.personalised.utils, idUtils);
  // The deprecated trackAuthenticationChanges function has been removed
  // It was replaced by trackLoginState and trackLoginEvent functions
  
  /**
   * Safely run a callback when document.body is available
   * @param {Function} callback - The function to run when document.body is available
   */
  function whenBodyReady(callback) {
    if (typeof callback !== 'function') {
      console.error('[DOM Utils] whenBodyReady requires a function callback');
      return;
    }
    
    // If body is already available, run immediately
    if (document.body) {
      callback(document.body);
      return;
    }
    
    // Otherwise wait for DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        if (document.body) {
          callback(document.body);
        }
      });
    } else {
      // Use MutationObserver as a last resort if document is loaded but body not available
      // (rare edge case)
      const observer = new MutationObserver(() => {
        if (document.body) {
          callback(document.body);
          observer.disconnect();
        }
      });
      
      observer.observe(document.documentElement, { childList: true });
    }
  }
  
  /**
   * Check if user is currently logged in
   * Uses personalisedCore implementation when available
   * @returns {boolean} True if user is logged in, false otherwise
   */
  function isUserLoggedIn() {
    if (window.personalisedCore?.loginTracking?.isUserLoggedIn) {
      return window.personalisedCore.loginTracking.isUserLoggedIn();
    }
    
    // Fallback implementation if core isn't available
    if (document.body) {
      return document.body.dataset.customerLoggedIn === 'true';
    }
    
    return localStorage.getItem('personalised_user_logged_in') === 'true';
  }

  // Add functions to the utils namespace
  Object.assign(window.personalised.utils, {
    whenBodyReady,
    isUserLoggedIn
  });
  
  // All storage access now goes through storageManager

  /**
   * Validates that all required metadata exists for edit mode
  
  
   * If metadata is missing but subscription ID is available, attempts to recover from Recharge
   * @returns {Promise<boolean>} True if metadata is valid or was successfully recovered
   */
  async function validateMetadataIntegrity() {
    try {
      // Only check metadata in edit mode
      const mode = window.personalised.utils.getMode();
      if (mode !== window.personalised.config.modes.EDIT) {
        return true; // Not in edit mode, no validation needed
      }
      
      // Get subscription ID from session
      const subscriptionId = window.personalised.utils.getCurrentSubscriptionId();
      if (!subscriptionId) {
        console.warn('[Metadata Integrity] In edit mode but no subscription ID found');
        return false;
      }
      
      // Check if critical metadata is missing
      const hasBundleSelectionId = window.personalised.bundle.hasMetadata('bundleSelectionId');
      const hasSubscriptionId = window.personalised.bundle.hasMetadata('subscriptionId');
      const hasPurchaseItemId = window.personalised.bundle.hasMetadata('purchaseItemId');
      
      const metadataIntact = hasBundleSelectionId && hasSubscriptionId && hasPurchaseItemId;
      
      // If metadata is intact, no recovery needed
      if (metadataIntact) {
        console.debug('[Metadata Integrity] All critical metadata is present');
        return true;
      }
      
      // Log the missing metadata for debugging
      console.warn('[Metadata Integrity] Missing critical metadata detected:', {
        hasBundleSelectionId,
        hasSubscriptionId,
        hasPurchaseItemId,
        mode,
        subscriptionId
      });
      
      // Attempt to recover metadata from Recharge
      console.log('[Metadata Integrity] Attempting to recover metadata from Recharge');
      
      if (!window.personalised.rechargeUtils?.getBundleSelectionForSubscription) {
        console.error('[Metadata Integrity] Cannot recover metadata: rechargeUtils not available');
        return false;
      }
      
      // Get fresh data from Recharge
      const bundleData = await window.personalised.rechargeUtils.getBundleSelectionForSubscription(subscriptionId);
      
      if (!bundleData) {
        console.error('[Metadata Integrity] Failed to fetch bundle data from Recharge');
        return false;
      }
      
      // Store critical metadata
      if (bundleData.bundle) {
        window.personalised.bundle.setMetadata('bundleSelectionId', bundleData.bundle.id);
        window.personalised.bundle.setMetadata('purchaseItemId', bundleData.bundle.purchaseItemId);
        window.personalised.bundle.setMetadata('externalProductId', bundleData.bundle.external_product_id);
        window.personalised.bundle.setMetadata('externalVariantId', bundleData.bundle.external_variant_id);
        
        if (bundleData.bundle.bundleRechargeId) {
          window.personalised.bundle.setMetadata('bundleRechargeId', bundleData.bundle.bundleRechargeId);
        }
      }
      
      if (bundleData.subscription) {
        window.personalised.bundle.setMetadata('subscriptionId', bundleData.subscription.id);
        window.personalised.bundle.setMetadata('originalFrequency', bundleData.subscription.order_interval_frequency);
      }
      
      console.log('[Metadata Integrity] Successfully recovered metadata from Recharge');
      
      // Verify recovery was successful
      const recoverySuccessful = 
        window.personalised.bundle.hasMetadata('bundleSelectionId') &&
        window.personalised.bundle.hasMetadata('subscriptionId') &&
        window.personalised.bundle.hasMetadata('purchaseItemId');
      
      return recoverySuccessful;
    } catch (err) {
      console.error('[Metadata Integrity] Error validating metadata integrity:', err);
      return false;
    }
  }
  
  // Add validateMetadataIntegrity to the utils namespace
  Object.assign(window.personalised.utils, { validateMetadataIntegrity });
  
  /**
   * Handles authentication errors by clearing bundle data
   * Should be called when API calls fail with authentication errors
   * @param {Error} error - The error that occurred
   * @param {string} source - Source of the error for logging
   * @returns {boolean} True if handled as authentication error
   */
  function handleAuthError(error, source = 'unknown') {
    try {
      // Check if error is authentication-related
      const errorMessage = error?.message || String(error);
      const isAuthError =
        errorMessage.includes('authentication') ||
        errorMessage.includes('unauthorized') ||
        errorMessage.includes('not logged in') ||
        errorMessage.includes('auth') ||
        errorMessage.includes('401');

      if (!isAuthError) {
        return false; // Not an authentication error
      }

      console.warn(`[Auth Error] Authentication error detected in ${source}:`, errorMessage);

      // Clear bundle data
      if (window.personalised?.bundle?.teardown) {
        window.personalised.bundle.teardown();
        console.log('[Auth Error] Bundle data cleared due to authentication error');

        // Also clear customer ID to force redetection
        sessionStorage.removeItem('personalised_customer_id');

        return true;
      }

      return false;
    } catch (err) {
      console.error('[Auth Error] Error in handleAuthError:', err);
      return false;
    }
  }
  
  // Add handleAuthError to the utils namespace
  Object.assign(window.personalised.utils, { handleAuthError });
  
  /**
   * Waits for the Recharge SDK to be fully initialized and ready to use
   * @param {number} timeout - Maximum time to wait in milliseconds (default: 10000ms)
   * @returns {Promise<boolean>} - Resolves true if SDK is ready, rejects on timeout or failure
   */
  function waitForRechargeSDK(timeout = 10000) {
    return new Promise((resolve, reject) => {
      if (window.rechargeInitialized && window.recharge) {
        console.debug('[SDK Wait] Recharge already initialized');
        return resolve(true);
      }

      const timeoutId = setTimeout(() => {
        console.error('[SDK Wait] Recharge SDK timeout');
        reject(new Error('Recharge SDK did not initialize in time'));
      }, timeout);

      // Listen if it's already initializing
      if (window.rechargeInitializing) {
        window.addEventListener('recharge:ready', () => {
          clearTimeout(timeoutId);
          console.debug('[SDK Wait] Recharge SDK ready via event');
          resolve(true);
        }, { once: true });
        return;
      }

      // Poll until it begins initializing, then hook into event
      let attempts = 0;
      const intervalId = setInterval(() => {
        attempts++;

        if (window.rechargeInitialized && window.recharge) {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          console.debug('[SDK Wait] Recharge SDK became available via poll');
          return resolve(true);
        }

        if (window.rechargeInitializing) {
          clearInterval(intervalId);
          window.addEventListener('recharge:ready', () => {
            clearTimeout(timeoutId);
            console.debug('[SDK Wait] Recharge SDK ready via event after polling');
            resolve(true);
          }, { once: true });
        }
      }, 100);
    });
  }
  
  // Add waitForRechargeSDK to the utils namespace
  if (window.personalised?.utils) {
    window.personalised.utils.waitForRechargeSDK = waitForRechargeSDK;
    console.debug('[SDK Wait] Recharge SDK utility initialized');
  }
  
  /**
   * Checks if a URL contains the portal path
   * @param {string} [url] - Optional URL to check, defaults to current location
   * @returns {boolean} - True if URL contains portal path
   */
  function isPortalUrl(url) {
    return (url || window.location.href).includes('/tools/recurring/portal/');
  }
  
  /**
   * Lightweight wrapper for checkForPortalReturn that delegates to the version in personalised-login-verification.js
   * @returns {boolean} True if portal return was detected
   */
  function checkForPortalReturn() {
    // Delegate to the global implementation in personalised-login-verification.js
    // This prevents duplication of logic while ensuring the function can still be called from utils
    if (window.personalised?.checkForPortalReturn) {
      return window.personalised.checkForPortalReturn();
    }
    
    // Minimal fallback only if login verification hasn't loaded yet
    // Simply sets the freshness flag without doing URL rewriting or duplicate logging
    const portalReturnParam = new URLSearchParams(window.location.search).get('portal_return');
    if (portalReturnParam === 'true' || isPortalUrl(window.location.href)) {
      console.debug('[Personalised] Portal return detected (utils fallback) — setting session freshness flag');
      sessionStorage.setItem('personalised_session_fresh', 'true');
      return true;
    }
    
    return false;
  }
  
  /**
   * Refresh only Recharge session and bundle cache — used when returning from portal.
   * Preserves user mode, subscription ID, and login state.
   * @param {string} reason - Reason for refreshing the session (for logging)
   */
  function refreshSessionTokensOnly(reason = 'manual') {
    // Remove Recharge session from memory and storage
    if (window.personalised.sessionManager) {
      delete window.personalised.sessionManager.currentSession;
    }

    sessionStorage.removeItem('personalised_recharge_token');
    sessionStorage.setItem('personalised_session_fresh', 'true');

    // Clear localStorage bundle caches only — do not clear mode or subscription_id
    localStorage.removeItem('personalised_bundle_items');
    localStorage.removeItem('personalised_bundle_summary');
    localStorage.removeItem('personalised_bundle_error');

    // Clear in-memory state if applicable
    if (window.personalised.bundleState?.reset) {
      window.personalised.bundleState.reset();
    }

    console.log(`[Personalised] Session and bundle cache refreshed (${reason}). Mode and subscription preserved.`);
  }

  // Add portal utilities to the utils namespace
  if (window.personalised?.utils) {
    window.personalised.utils.isPortalUrl = isPortalUrl;
    window.personalised.utils.checkForPortalReturn = checkForPortalReturn;
    window.personalised.utils.refreshSessionTokensOnly = refreshSessionTokensOnly;
    
    // IMPORTANT: Define fullInitMode before we try to use it
    // This is the full implementation that will replace the placeholder
    /**
     * Initializes the user's interaction mode ("guest" or "edit").
     * Guarantees safe, memoized, idempotent async initialization.
     * Ensures Recharge SDK and session are available before subscription checks.
     *
     * @returns {Promise<'guest' | 'edit'>} The resolved user mode.
     */
    const fullInitMode = async function() {
      if (window.personalised.debugMode) console.debug('[initMode] Called - checking current mode');
      
      // Check if we already have a mode in storage
      if (window.personalised.storageManager.hasStoredMode()) {
        const currentMode = window.personalised.utils.getMode();
        if (window.personalised.debugMode) console.debug(`[initMode] Mode found in storage: ${currentMode}`);
        return currentMode;
      }
      
      if (window.personalised.debugMode) console.debug('[initMode] No stored mode found, detecting mode...');
      
      // Check authentication status
      const isAuthenticated = window.personalised.sessionManager?.isAuthenticatedSession() || 
                            !!window.personalised.customerIdManager.get() ||
                            (document.body?.dataset?.customerLoggedIn === 'true');
      
      if (!isAuthenticated) {
        // No authentication = guest mode
        if (window.personalised.debugMode) console.info('[Mode] Setting mode to guest (no authentication)');
        window.personalised.utils.setCurrentMode('guest', { source: 'ensureModeInitialized:noAuth' });
        return 'guest';
      }
      
      // We have an authenticated user, check for subscription
      try {
        // 1. Wait for Recharge SDK to be ready
        if (typeof window.personalised.utils.waitForRechargeSDK === 'function') {
          if (window.personalised.debugMode) console.debug('[initMode] Waiting for Recharge SDK...');
          try {
            await window.personalised.utils.waitForRechargeSDK();
          } catch (sdkError) {
            console.error('[initMode] Recharge SDK failed to initialize:', sdkError);
            throw new Error('Recharge SDK not available');
          }
        }
        
        // Verify SDK is available after waiting
        if (!window.recharge?.auth) {
          throw new Error('Recharge SDK auth module not available');
        }
        
        // 2. Validate/establish Recharge session using loginShopifyAppProxy (SDK v1.38.0)
        let session = null;
        try {
          if (window.personalised.debugMode) console.debug('[initMode] Attempting to get session via loginShopifyAppProxy...');
          session = await window.recharge.auth.loginShopifyAppProxy();
        } catch (loginError) {
          console.error('[initMode] Failed to establish Recharge session:', loginError);
          throw new Error('Unable to establish Recharge session');
        }
        if (!session) {
          throw new Error('Failed to obtain valid Recharge session');
        }
        
        // 3. Check for subscription with retry
        if (typeof window.personalised.rechargeUtils?.findPersonalisedPackSubscription === 'function') {
          let attempts = 0;
          let lastError;
          
          while (attempts < 3) {
            try {
              if (attempts > 0) {
                if (window.personalised.debugMode) console.debug(`[initMode] Retrying subscription check (attempt ${attempts + 1})...`);
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
              
              const pack = await window.personalised.rechargeUtils.findPersonalisedPackSubscription();
              
              if (pack) {
                // Subscription found - edit mode
                if (window.personalised.debugMode) console.info(`[Mode] Setting mode to edit with subscription ID: ${pack.id}`);
                window.personalised.utils.setCurrentMode('edit', { 
                  source: 'ensureModeInitialized:subscription', 
                  subscriptionId: pack.id
                });
                return 'edit';
              }
              
              // No subscription found, no need to retry
              break;
              
            } catch (error) {
              lastError = error;
              attempts++;
              if (attempts === 3) {
                console.error('[initMode] All subscription check retries failed:', error);
                throw error;
              }
            }
          }
          
          // No subscription after retries - new mode
          if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (authenticated without subscription)');
          window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:noSubscription' });
          return 'new';
        } else {
          // Subscription check not available
          if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (subscription check unavailable)');
          window.personalised.utils.setCurrentMode('new', { source: 'ensureModeInitialized:noSubCheck' });
          return 'new';
        }
      } catch (error) {
        // Log the specific error for debugging
        if (window.personalised.debugMode) {
          console.warn('[Mode] Error during mode initialization:', {
            error,
            hasRechargeSDK: !!window.recharge,
            hasAuthModule: !!window.recharge?.auth,
            hasSession: !!(window.recharge?.auth?.getSession && await window.recharge.auth.getSession().catch(() => null))
          });
        }
        
        // Error but authenticated = new mode
        if (window.personalised.debugMode) console.info('[Mode] Setting mode to new (error during subscription check)');
        window.personalised.utils.setCurrentMode('new', { 
          source: 'ensureModeInitialized:error',
          error: error.message
        });
        return 'new';
      }
    };
    
    // Now that fullInitMode is defined, let's use it to replace the placeholder
    if (window.personalised?.debugMode) {
      console.debug('[Mode] fullInitMode is defined:', typeof fullInitMode === 'function');
    }
    
    // Replace the placeholder initMode from personalised-mode.js with our full implementation
    // Only if it exists to ensure safe overwriting
    if (typeof window.personalised.utils.initMode === 'function') {
      try {
        // Get reference to the original implementation
        const placeholderFunction = window.personalised.utils.initMode.toString();
        
        if (window.personalised?.debugMode) {
          console.debug('[Mode] Current initMode implementation:', placeholderFunction.slice(0, 100) + '...');
        }
        
        // Check if it's still the placeholder (contains 'placeholder' in the function definition)
        const isStillPlaceholder = placeholderFunction.includes('Placeholder') || 
                                  placeholderFunction.includes('placeholder');
        
        if (isStillPlaceholder) {
          if (window.personalised?.debugMode) {
            console.debug('[Mode] Confirmed current implementation is placeholder, replacing with full version');
          }
          
          // Replace with our full implementation
          window.personalised.utils.initMode = fullInitMode;
          
          if (window.personalised?.debugMode) {
            console.info('[Mode] initMode implementation replaced with full version from personalised-utils.js');
          }
        } else {
          // Skip replacement since it's already been replaced
          if (window.personalised?.debugMode) {
            console.debug('[Mode] initMode already has a full implementation, skipping replacement');
          }
        }
      } catch (error) {
        console.error('[Mode] Error while replacing initMode placeholder:', error);
      }
    } else {
      console.error('[Mode] Could not find initMode placeholder from personalised-mode.js - mode functionality may be broken');
    }
  }
  
  // Check for portal return on page load
  document.addEventListener('DOMContentLoaded', () => {
    if (window.personalised?.utils?.checkForPortalReturn) {
      window.personalised.utils.checkForPortalReturn();
    }
  });
})();
